import { useState, useMemo } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight, Store, Info, ArrowLeft, ChevronUp, ChevronDown } from 'lucide-react';
import { useMarketplace } from '../../context/MarketplaceContext';
import { getProductById, getMerchantById, COMMISSION_RATES, PRODUCTS } from '../../data/marketplaceData';
import ProductCard from './ProductCard';

export default function CartDrawer() {
  const {
    showCart,
    setShowCart,
    cart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    getCartTotals,
    setShowCheckout,
  } = useMarketplace();

  const [showCommissionInfo, setShowCommissionInfo] = useState(false);
  const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(true);

  const totals = getCartTotals();

  // Get related products based on cart items (same/complementary categories)
  const relatedProducts = useMemo(() => {
    if (!cart || !cart.items || cart.items.length === 0) return [];

    // Get categories and tags from cart items
    const cartCategories = new Set();
    const cartTags = new Set();
    const cartProductIds = new Set();

    cart.items.forEach((item) => {
      const product = getProductById(item.productId);
      if (!product) return;
      cartProductIds.add(product.id);
      if (product.category) {
        cartCategories.add(product.category);
      }
      if (product.tags) {
        product.tags.forEach((tag) => cartTags.add(tag));
      }
    });

    // Find related products not in cart
    return PRODUCTS.filter((product) => {
      if (cartProductIds.has(product.id)) return false;

      // Same category or shared tags
      const sameCategory = cartCategories.has(product.category);
      const sharedTags = product.tags?.some((tag) => cartTags.has(tag));

      return sameCategory || sharedTags;
    }).slice(0, 6);
  }, [cart.items]);

  const handleCheckout = () => {
    setShowCart(false);
    setShowCheckout(true);
  };

  const handleClose = () => {
    setShowCart(false);
  };

  if (!showCart) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={handleClose}
      />

      {/* Full Screen Slider */}
      <div className="fixed inset-0 bg-white z-50 animate-slide-in-right overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center gap-2">
              <ShoppingBag size={20} className="text-indigo-600" />
              <h2 className="font-bold text-lg text-gray-900">Your Cart</h2>
              {totals.itemCount > 0 && (
                <span className="bg-indigo-100 text-indigo-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {totals.itemCount}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto transition-all duration-300 ${orderSummaryExpanded ? 'pb-72' : 'pb-36'}`}>
          {totals.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <ShoppingBag size={32} className="text-gray-400" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Your cart is empty</h3>
              <p className="text-gray-500 text-sm mb-6">Start shopping to add items to your cart</p>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="max-w-lg mx-auto">
              <div className="p-4 space-y-4">
                {/* Referral Attribution Banner */}
                {totals.referralMerchant && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-3 border border-indigo-100">
                    <div className="flex items-start gap-2">
                      <Store size={16} className="text-indigo-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-bold text-indigo-700">
                          Referred by {totals.referralMerchant.name}
                        </p>
                        <p className="text-[10px] text-indigo-600">
                          They'll earn commission for introducing you to the marketplace
                        </p>
                      </div>
                      <button
                        onClick={() => setShowCommissionInfo(!showCommissionInfo)}
                        className="p-1 text-indigo-600 hover:bg-indigo-100 rounded-full"
                      >
                        <Info size={14} />
                      </button>
                    </div>

                    {showCommissionInfo && (
                      <div className="mt-3 pt-3 border-t border-indigo-200 text-xs text-indigo-700">
                        <p className="mb-2 font-bold">How the commission model works:</p>
                        <ul className="space-y-1 text-[11px]">
                          <li>- When you buy from a different merchant, the referral merchant earns {COMMISSION_RATES.REFERRAL * 100}% commission</li>
                          <li>- The selling merchant receives {COMMISSION_RATES.SELLER * 100}% of the sale</li>
                          <li>- This encourages merchants to share customers, benefiting everyone!</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Cart Items */}
                <div className="space-y-3">
                  {totals.items.map(item => (
                    <CartItem
                      key={item.productId}
                      item={item}
                      onUpdateQuantity={updateCartQuantity}
                      onRemove={removeFromCart}
                    />
                  ))}
                </div>

                {/* Clear Cart */}
                <button
                  onClick={clearCart}
                  className="flex items-center gap-2 text-red-500 text-sm font-medium hover:text-red-600"
                >
                  <Trash2 size={14} />
                  Clear Cart
                </button>

                {/* Commission Breakdown (for transparency) */}
                {totals.commissionBreakdown.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">
                      Commission Breakdown
                    </h4>
                    <div className="space-y-2">
                      {/* Seller Revenue - for each merchant selling products */}
                      {totals.commissionBreakdown.map(group => (
                        <div key={group.merchantId} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">{group.merchant?.name} Revenue</span>
                          <span className="font-bold text-gray-900">
                            ${group.sellerAmount.toFixed(2)}
                          </span>
                        </div>
                      ))}

                      {/* Referral Commission - only if referral merchant is different from sellers */}
                      {totals.referralMerchant && totals.commissionBreakdown.some(g => g.referralCommission > 0) && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">{totals.referralMerchant.name} Commission</span>
                          <span className="font-bold text-indigo-600">
                            ${totals.commissionBreakdown.reduce((sum, g) => sum + (g.referralCommission || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Marketplace Commission */}
                      <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Marketplace Commission</span>
                        <span className="font-bold text-gray-900">
                          ${totals.commissionBreakdown.reduce((sum, g) => sum + (g.marketplaceCommission || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Related Products Section */}
              {relatedProducts.length > 0 && (
                <div className="p-4 border-t border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3">Related Products</h3>
                  <p className="text-xs text-gray-500 mb-3">You might also like these items</p>
                  <div className="grid grid-cols-2 gap-3">
                    {relatedProducts.slice(0, 4).map((product) => (
                      <ProductCard key={product.id} product={product} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed Bottom - Complete Your Order Summary (Expandable) */}
        {totals.items.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 border-t border-gray-100 bg-white z-10 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="max-w-lg mx-auto p-4">
              {/* Expandable Header */}
              <button
                onClick={() => setOrderSummaryExpanded(!orderSummaryExpanded)}
                className="w-full flex items-center justify-between mb-3"
              >
                <h3 className="font-bold text-gray-900">Complete Your Order</h3>
                <div className="flex items-center gap-2">
                  {!orderSummaryExpanded && (
                    <span className="font-black text-xl text-gray-900">${totals.total.toFixed(2)}</span>
                  )}
                  {orderSummaryExpanded ? (
                    <ChevronDown size={20} className="text-gray-400" />
                  ) : (
                    <ChevronUp size={20} className="text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Order Summary Details */}
              <div className={`overflow-hidden transition-all duration-300 ${orderSummaryExpanded ? 'max-h-96 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal ({totals.itemCount} items)</span>
                    <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Shipping</span>
                    <span className={totals.shipping === 0 ? 'text-emerald-600' : 'text-gray-900'}>
                      {totals.shipping === 0 ? 'FREE' : `$${totals.shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Tax (8%)</span>
                    <span className="text-gray-900">${totals.tax.toFixed(2)}</span>
                  </div>

                  {/* Free Shipping Progress */}
                  {totals.subtotal < 50 && (
                    <div className="bg-amber-50 rounded-lg p-2 mt-2">
                      <p className="text-xs text-amber-700">
                        Add ${(50 - totals.subtotal).toFixed(2)} more for <span className="font-bold">FREE shipping</span>
                      </p>
                      <div className="mt-1.5 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${Math.min((totals.subtotal / 50) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="font-black text-xl text-gray-900">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <span>Proceed to Checkout</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function CartItem({ item, onUpdateQuantity, onRemove }) {
  const product = item.product;
  const merchant = getMerchantById(product.merchantId);

  return (
    <div className="flex gap-3 bg-white rounded-xl border border-gray-100 p-3">
      {/* Image */}
      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold text-indigo-600 uppercase">{merchant?.name}</p>
        <h4 className="font-bold text-sm text-gray-900 line-clamp-1">{product.name}</h4>
        <p className="font-black text-gray-900 mt-1">${product.price.toFixed(2)}</p>

        {/* Quantity Controls */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => onUpdateQuantity(item.productId, item.quantity - 1)}
              className="p-1.5 hover:bg-gray-200 rounded-lg"
            >
              <Minus size={14} className="text-gray-600" />
            </button>
            <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
            <button
              onClick={() => onUpdateQuantity(item.productId, item.quantity + 1)}
              className="p-1.5 hover:bg-gray-200 rounded-lg"
              disabled={item.quantity >= product.stock}
            >
              <Plus size={14} className={item.quantity >= product.stock ? 'text-gray-300' : 'text-gray-600'} />
            </button>
          </div>

          <button
            onClick={() => onRemove(item.productId)}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
